import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/layout/Header';
import { showError, confirmAction } from '@/lib/swal';
import {
  ArrowLeft,
  RefreshCw,
  Eye,
  Ban,
  Info,
  Loader2,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import { resolveSlickPayBankDetails, type UnitPaymentMethods } from '@masonic-tools/shared-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PoStatus =
  | 'created'
  | 'linkCreated'
  | 'openBankingStarted'
  | 'cardStarted'
  | 'acceptedInProgress'
  | 'acceptedComplete'
  | 'acceptedRejected'
  | 'voided'
  | 'initiated'
  | 'failed'
  | 'superseded';

type OrderType = 'invoice' | 'credit_purchase';

interface SlickPayOrder {
  id: string;
  poId?: string;       // absent on initiated/failed records (no PIS poId yet)
  requestId?: string;  // local UUID written at function start
  error?: string;      // set on failed records
  pisStatusCode?: number;
  pisErrorCode?: number;
  pisErrorName?: string;
  pisErrorMessage?: string;
  pisStep?: string;
  pisError?: unknown;
  note?: string;
  type: OrderType;
  unitId: string;
  // invoice fields
  eventId?: string;
  invoiceId?: string;
  // credit purchase fields
  packId?: string;
  credits?: number;
  // common
  amountPence?: number;
  totalAmountPence?: number;
  processingFeePence?: number;
  creditorReference?: string;
  /** PIS v1 statement reference (replaces creditorReference) */
  statementReference?: string;
  creditorId?: string;
  creditorAccountName?: string;
  creditorSortCode?: string;
  creditorAccountNumber?: string;
  /** Hope Macy creditor account UUID the PO was created against */
  creditorAccountId?: string;
  status: PoStatus;
  createdAt?: Timestamp;
  completedAt?: Timestamp;
  voidedAt?: Timestamp;
  updatedAt?: Timestamp;
  paidAmountPence?: number;
  initiatedByUserId?: string | null;
  debtorEmailAddress?: string;
  /** Guest's full name, taken from the RSVP linked to the invoice (invoice orders only). */
  debtorName?: string;
  voidedByAdminUid?: string;
  tld?: 'org' | 'com';
  pisApplicationId?: string;
  pisCredentialSource?: 'secret_manager' | 'firestore' | 'environment' | 'unknown';
  diagnosticReport?: string;
  diagnosticEgressIp?: string;
  diagnosticExpectedEgressIp?: string;
  diagnosticEgressIpMatch?: boolean;
  diagnosticCreateSessionOk?: boolean;
  diagnosticPisErrorCode?: number;
  activityLog?: OrderActivityEntry[];
  pisHistory?: PisHistoryRow[];
  pisRawStatus?: string;
  pisLastSnapshot?: Record<string, unknown>;
}

interface OrderActivityEntry {
  id?: string;
  at?: unknown;
  action?: string;
  actorUid?: string;
  actorLabel?: string;
  message?: string;
  pisStatus?: string;
  meta?: Record<string, unknown>;
}

interface PisHistoryRow {
  time?: string;
  event?: string;
  details?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function orderStatementRef(order: Pick<SlickPayOrder, 'statementReference' | 'creditorReference'>): string | undefined {
  return order.statementReference ?? order.creditorReference;
}

function fmtDate(v: unknown): string {
  if (v instanceof Timestamp) return format(v.toDate(), 'dd MMM yyyy HH:mm');
  if (v && typeof v === 'object' && 'toDate' in v) {
    return format((v as Timestamp).toDate(), 'dd MMM yyyy HH:mm');
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return format(d, 'dd MMM yyyy HH:mm');
  }
  return '—';
}

function fmtGbp(pence: number | undefined): string {
  if (pence == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
}

interface DestinationBankDetails {
  nameOnAccount?: string;
  sortCode?: string;
  accountNumber?: string;
}

function formatSortCodeDisplay(sortCode: string): string {
  const digits = sortCode.replace(/[-\s]/g, '');
  if (digits.length !== 6) return sortCode;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

function parseSortCodeAccountId(id: string): { sortCode: string; accountNumber: string } | null {
  const digits = id.replace(/[-\s]/g, '');
  if (!/^\d{14}$/.test(digits)) return null;
  return { sortCode: digits.slice(0, 6), accountNumber: digits.slice(6) };
}

function resolveDestinationBankDetails(
  order: SlickPayOrder,
  unitPaymentMethods?: UnitPaymentMethods,
): DestinationBankDetails {
  let nameOnAccount = order.creditorAccountName?.trim() || undefined;
  let sortCode = order.creditorSortCode?.trim() || undefined;
  let accountNumber = order.creditorAccountNumber?.trim() || undefined;

  if (!nameOnAccount || !sortCode || !accountNumber) {
    const snap = order.pisLastSnapshot?.creditorAccount as { id?: string; name?: string } | undefined;
    if (snap) {
      nameOnAccount = nameOnAccount || snap.name?.trim() || undefined;
      const parsed = snap.id ? parseSortCodeAccountId(snap.id) : null;
      sortCode = sortCode || parsed?.sortCode;
      accountNumber = accountNumber || parsed?.accountNumber;
    }
  }

  if ((!nameOnAccount || !sortCode || !accountNumber) && order.type === 'invoice' && unitPaymentMethods) {
    const unitBank = resolveSlickPayBankDetails(unitPaymentMethods);
    nameOnAccount = nameOnAccount || unitBank.nameOnAccount || undefined;
    if (!sortCode && unitBank.sortCode) {
      sortCode = unitBank.sortCode.replace(/[-\s]/g, '') || undefined;
    }
    if (!accountNumber && unitBank.accountNumber) {
      accountNumber = unitBank.accountNumber.replace(/[-\s]/g, '') || undefined;
    }
  }

  return { nameOnAccount, sortCode, accountNumber };
}

function statusVariant(s: PoStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'acceptedComplete') return 'default';
  if (s === 'acceptedRejected' || s === 'failed') return 'destructive';
  if (s === 'voided' || s === 'superseded') return 'secondary';
  return 'outline';
}

function statusLabel(s: PoStatus): string {
  const map: Record<PoStatus, string> = {
    created: 'Created',
    linkCreated: 'Link created',
    openBankingStarted: 'OB started',
    cardStarted: 'Card started',
    acceptedInProgress: 'In progress',
    acceptedComplete: 'Complete',
    acceptedRejected: 'Rejected',
    voided: 'Voided',
    initiated: 'Initiated',
    failed: 'Failed',
    superseded: 'Superseded',
  };
  return map[s] ?? s;
}

function typeLabel(t: OrderType): string {
  return t === 'credit_purchase' ? 'Credit purchase' : 'Invoice';
}

interface GuestDisplay {
  name?: string;
  email?: string;
}

/** Invoice orders are paid by a dining guest; credit purchases have no individual guest. */
function resolveGuest(
  order: SlickPayOrder,
  guestInfo: Record<string, { name?: string; email?: string }>,
): GuestDisplay | null {
  if (order.type !== 'invoice') return null;
  const fallback = guestInfo[canonicalPoKey(order)];
  const name = order.debtorName || fallback?.name;
  const email = order.debtorEmailAddress || fallback?.email;
  if (!name && !email) return null;
  return { name, email };
}

/** Matches PIS spec: void allowed while created / linkCreated / openBankingStarted / cardStarted. */
const VOIDABLE: PoStatus[] = ['created', 'linkCreated', 'openBankingStarted', 'cardStarted'];

const TERMINAL_STATUSES: PoStatus[] = [
  'acceptedComplete',
  'acceptedRejected',
  'voided',
  'failed',
  'superseded',
];

function canonicalPoKey(order: SlickPayOrder): string {
  return order.poId || order.requestId || order.id;
}

function orderDisplayRank(order: SlickPayOrder): number {
  if (order.id === order.poId) return 100;
  if (order.status === 'acceptedComplete') return 95;
  if (order.status === 'acceptedInProgress') return 85;
  if (order.status === 'linkCreated') return 80;
  if (order.status === 'superseded') return 10;
  if (order.status === 'initiated') return 5;
  return 50;
}

/** One row per PO — prefer canonical slickpayOrders/{poId} over superseded request stub. */
function dedupeOrders(orders: SlickPayOrder[]): SlickPayOrder[] {
  const map = new Map<string, SlickPayOrder>();
  for (const o of orders) {
    const key = canonicalPoKey(o);
    const existing = map.get(key);
    if (!existing || orderDisplayRank(o) > orderDisplayRank(existing)) {
      map.set(key, o);
    }
  }
  return [...map.values()];
}

function activityActionLabel(action: string | undefined): string {
  const map: Record<string, string> = {
    checkout_initiated: 'Checkout started',
    checkout_validated: 'Validated',
    pis_create_order: 'PIS create order',
    pis_create_link: 'PIS create link',
    link_created: 'Link created',
    status_sync: 'Status synced',
    status_poll: 'Status polled',
    admin_refresh: 'Admin refresh',
    callback_received: 'Callback received',
    credits_awarded: 'Credits awarded',
    payment_recorded: 'Payment recorded',
    diagnostic_run: 'Diagnostic run',
    failed: 'Failed',
    voided: 'Voided',
  };
  return map[action ?? ''] ?? action ?? 'Event';
}

function pisEventLabel(event: string | undefined): string {
  const map: Record<string, string> = {
    created: 'Order created',
    linkCreated: 'Payment link created',
    payByBankStarted: 'Open Banking started',
    payByBankCallback: 'Bank callback',
    acquiredSessionCreated: 'Card session created',
    acquiredWebhook: 'Card webhook',
  };
  return map[event ?? ''] ?? event ?? 'Event';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SlickPayOrdersPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<SlickPayOrder[]>([]);
  const [unitLabels, setUnitLabels] = useState<Record<string, string>>({});
  const [guestInfo, setGuestInfo] = useState<Record<string, { name?: string; email?: string }>>({});

  // Filters (client-side)
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [search, setSearch] = useState('');

  // Detail dialog
  const [detailOrder, setDetailOrder] = useState<SlickPayOrder | null>(null);
  const [detailUnitName, setDetailUnitName] = useState<string>('');
  const [detailUnitPaymentMethods, setDetailUnitPaymentMethods] = useState<UnitPaymentMethods | undefined>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPisHistory, setDetailPisHistory] = useState<PisHistoryRow[]>([]);
  const [detailActivity, setDetailActivity] = useState<OrderActivityEntry[]>([]);

  // Refund info dialog
  const [refundOrder, setRefundOrder] = useState<SlickPayOrder | null>(null);

  // Void in progress
  const [voidingPoId, setVoidingPoId] = useState<string | null>(null);
  const [refreshingPoId, setRefreshingPoId] = useState<string | null>(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'slickpayOrders'),
        orderBy('createdAt', 'desc'),
        limit(300),
      );
      const snap = await getDocs(q);
      const list: SlickPayOrder[] = [];
      snap.forEach((d) => {
        list.push({ id: d.id, ...(d.data() as Omit<SlickPayOrder, 'id'>) });
      });
      const deduped = dedupeOrders(list);
      setOrders(deduped);

      const uniqueUnitIds = [...new Set(list.map((o) => o.unitId).filter(Boolean))].slice(0, 150);
      const entries = await Promise.all(
        uniqueUnitIds.map(async (uid) => {
          const us = await getDoc(doc(db, 'units', uid));
          const name = us.exists() ? String((us.data() as { name?: string }).name ?? uid) : uid;
          return [uid, name] as const;
        }),
      );
      setUnitLabels(Object.fromEntries(entries));

      // Older invoice orders were created before the guest's name/email was persisted
      // on the canonical order doc — best-effort backfill via the linked invoice/RSVP.
      const needsGuestLookup = deduped
        .filter((o) => o.type === 'invoice' && o.unitId && o.eventId && o.invoiceId && (!o.debtorName || !o.debtorEmailAddress))
        .slice(0, 150);
      const guestInfoUpdate: Record<string, { name?: string; email?: string }> = {};
      await Promise.all(
        needsGuestLookup.map(async (o) => {
          try {
            const invoiceSnap = await getDoc(doc(db, 'units', o.unitId, 'events', o.eventId!, 'invoices', o.invoiceId!));
            if (!invoiceSnap.exists()) return;
            const rsvpIds = (invoiceSnap.data() as { rsvps?: string[] }).rsvps;
            const rsvpId = Array.isArray(rsvpIds) ? rsvpIds[0] : undefined;
            if (!rsvpId) return;
            const rsvpSnap = await getDoc(doc(db, 'units', o.unitId, 'events', o.eventId!, 'rsvps', String(rsvpId)));
            if (!rsvpSnap.exists()) return;
            const rsvp = rsvpSnap.data() as { name?: string; email?: string };
            guestInfoUpdate[canonicalPoKey(o)] = { name: rsvp.name, email: rsvp.email };
          } catch {
            /* best-effort backfill only */
          }
        }),
      );
      setGuestInfo(guestInfoUpdate);
    } catch (e) {
      console.error(e);
      const code = (e as { code?: string }).code;
      if (code === 'permission-denied') {
        showError('Could not load SlickPay orders. Your account must have platform admin access (isAdmin on users/{uid}).');
      } else {
        showError('Could not load SlickPay orders. Ensure a Firestore index exists for slickpayOrders (createdAt desc).');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  // ---------------------------------------------------------------------------
  // Filtered rows
  // ---------------------------------------------------------------------------

  const filtered = orders.filter((o) => {
    // Request-level audit stubs are superseded once canonical slickpayOrders/{poId} exists.
    if (filterStatus === 'all' && o.status === 'superseded') return false;
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    if (filterType !== 'all' && o.type !== filterType) return false;
    if (search) {
      const s = search.toLowerCase();
      const guest = resolveGuest(o, guestInfo);
      const hit =
        o.poId?.toLowerCase().includes(s) ||
        o.requestId?.toLowerCase().includes(s) ||
        o.id?.toLowerCase().includes(s) ||
        o.pisErrorName?.toLowerCase().includes(s) ||
        o.pisErrorMessage?.toLowerCase().includes(s) ||
        String(o.pisErrorCode ?? '').includes(s) ||
        o.error?.toLowerCase().includes(s) ||
        orderStatementRef(o)?.toLowerCase().includes(s) ||
        o.creditorId?.toLowerCase().includes(s) ||
        o.debtorEmailAddress?.toLowerCase().includes(s) ||
        guest?.name?.toLowerCase().includes(s) ||
        guest?.email?.toLowerCase().includes(s) ||
        o.invoiceId?.toLowerCase().includes(s) ||
        (unitLabels[o.unitId] ?? o.unitId)?.toLowerCase().includes(s);
      if (!hit) return false;
    }
    return true;
  });

  // ---------------------------------------------------------------------------
  // Detail dialog
  // ---------------------------------------------------------------------------

  const openDetail = async (order: SlickPayOrder) => {
    const poId = order.poId || order.id;
    setDetailOrder(order);
    setDetailUnitName('');
    setDetailUnitPaymentMethods(undefined);
    setDetailPisHistory([]);
    setDetailActivity([]);
    setDetailLoading(true);
    try {
      const [unitSnap, canonicalSnap] = await Promise.all([
        getDoc(doc(db, 'units', order.unitId)),
        getDoc(doc(db, 'slickpayOrders', poId)),
      ]);
      if (unitSnap.exists()) {
        const unitData = unitSnap.data() as { name?: string; settings?: { paymentMethods?: UnitPaymentMethods } };
        setDetailUnitName(String(unitData.name ?? order.unitId));
        setDetailUnitPaymentMethods(unitData.settings?.paymentMethods);
      } else {
        setDetailUnitName(order.unitId);
      }
      if (canonicalSnap.exists()) {
        const fresh = { id: canonicalSnap.id, ...(canonicalSnap.data() as Omit<SlickPayOrder, 'id'>) };
        setDetailOrder(fresh);
        setDetailActivity(Array.isArray(fresh.activityLog) ? fresh.activityLog : []);
        setDetailPisHistory(Array.isArray(fresh.pisHistory) ? fresh.pisHistory : []);
      } else {
        setDetailActivity(Array.isArray(order.activityLog) ? order.activityLog : []);
        setDetailPisHistory(Array.isArray(order.pisHistory) ? order.pisHistory : []);
      }
    } catch {
      setDetailUnitName(order.unitId);
    } finally {
      setDetailLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Void action
  // ---------------------------------------------------------------------------

  const handleRunDiagnostic = async (order: SlickPayOrder) => {
    const requestId = order.requestId || order.id;
    setRunningDiagnostic(true);
    try {
      const fn = httpsCallable<{ requestId?: string; poId?: string }, { supportReport: string }>(
        functions,
        'diagnoseSlickPaySetup',
      );
      const res = await fn({ requestId, poId: order.poId || order.id });
      const report = res.data.supportReport;
      setDetailOrder((prev) =>
        prev ? { ...prev, diagnosticReport: report, requestId } : prev,
      );
      await navigator.clipboard.writeText(report);
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message || 'Diagnostic failed.';
      showError(msg);
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const handleRefreshStatus = async (order: SlickPayOrder) => {
    const poId = order.poId || order.id;
    if (!poId) return;

    setRefreshingPoId(poId);
    try {
      const fn = httpsCallable<
        { poId: string; attemptCompletion?: boolean },
        {
          firestoreStatus?: string;
          pisStatus?: string;
          completionAttempt?: { success: boolean; status: string; message?: string; credits?: number };
          activityLog?: OrderActivityEntry[];
          pisHistory?: PisHistoryRow[];
        }
      >(functions, 'adminInspectSlickPayOrder');
      const res = await fn({ poId, attemptCompletion: true });
      const data = res.data;

      const nextStatus = (data.firestoreStatus || data.pisStatus || order.status) as PoStatus;
      setOrders((prev) =>
        dedupeOrders(
          prev.map((o) =>
            canonicalPoKey(o) === poId
              ? { ...o, status: nextStatus, pisHistory: data.pisHistory, activityLog: data.activityLog }
              : o,
          ),
        ),
      );

      if (detailOrder && canonicalPoKey(detailOrder) === poId) {
        const refreshed = await getDoc(doc(db, 'slickpayOrders', poId));
        if (refreshed.exists()) {
          const fresh = { id: refreshed.id, ...(refreshed.data() as Omit<SlickPayOrder, 'id'>) };
          setDetailOrder(fresh);
          setDetailActivity(data.activityLog ?? fresh.activityLog ?? []);
          setDetailPisHistory(data.pisHistory ?? fresh.pisHistory ?? []);
        }
      }

      if (data.completionAttempt?.success) {
        return;
      }
      if (data.completionAttempt?.message) {
        showError(data.completionAttempt.message);
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message || 'Failed to refresh payment status.';
      showError(msg);
    } finally {
      setRefreshingPoId(null);
    }
  };

  const handleVoid = async (order: SlickPayOrder) => {
    if (!order.poId) return;

    const confirmed = await confirmAction({
      title: 'Void payment order?',
      text: `This will cancel PO ${order.poId} at the payment provider. The payer will no longer be able to complete this payment. This cannot be undone.`,
      confirmButtonText: 'Yes, void order',
      cancelButtonText: 'Cancel',
      icon: 'warning',
    });
    if (!confirmed.isConfirmed) return;

    setVoidingPoId(order.poId);
    try {
      const fn = httpsCallable<{ poId: string }, { success: boolean; message: string }>(
        functions,
        'adminVoidSlickPayOrder',
      );
      const res = await fn({ poId: order.poId });
      if (res.data.success) {
        setOrders((prev) =>
          prev.map((o) => (o.poId === order.poId ? { ...o, status: 'voided' as PoStatus } : o)),
        );
        if (detailOrder?.poId === order.poId) {
          setDetailOrder((prev) => (prev ? { ...prev, status: 'voided' as PoStatus } : prev));
        }
      } else {
        showError(res.data.message || 'Void failed.');
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message || 'Failed to void order.';
      showError(msg);
    } finally {
      setVoidingPoId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex gap-2">
      <span className="w-44 shrink-0 text-muted-foreground">{label}</span>
      <span className="break-all font-medium">{value ?? '—'}</span>
    </div>
  );

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-7xl">

          {/* Page header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Open Banking Orders</h1>
                <p className="text-muted-foreground mt-1">
                  All SlickPay PIS payment orders — invoices and credit purchases.
                </p>
              </div>
              <Button variant="outline" onClick={() => void loadOrders()} disabled={loading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8 w-64"
                placeholder="Search PO / request ID, ref, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="linkCreated">Link created</SelectItem>
                <SelectItem value="openBankingStarted">OB started</SelectItem>
                <SelectItem value="acceptedInProgress">In progress</SelectItem>
                <SelectItem value="acceptedComplete">Complete</SelectItem>
                <SelectItem value="acceptedRejected">Rejected</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="superseded">Superseded</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="credit_purchase">Credit purchase</SelectItem>
              </SelectContent>
            </Select>
            {(filterStatus !== 'all' || filterType !== 'all' || search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilterStatus('all'); setFilterType('all'); setSearch(''); }}
              >
                Clear filters
              </Button>
            )}
            <span className="text-sm text-muted-foreground ml-auto">
              {filtered.length} of {orders.length} orders
            </span>
          </div>

          {/* Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Payment orders</CardTitle>
              <CardDescription>
                Up to 300 most recent orders, newest first. Superseded request audit stubs are hidden unless you filter by that status.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                  <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                  Loading orders…
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-12 text-center text-muted-foreground">No orders found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Guest</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((order) => {
                        const guest = resolveGuest(order, guestInfo);
                        return (
                        <TableRow key={order.id}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {fmtDate(order.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="whitespace-nowrap text-xs">
                              {typeLabel(order.type)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">
                              {unitLabels[order.unitId] ?? order.unitId}
                            </div>
                            {order.type === 'credit_purchase' && order.packId && (
                              <div className="text-xs text-muted-foreground">Pack: {order.packId}</div>
                            )}
                            {order.type === 'invoice' && order.invoiceId && (
                              <div className="text-xs text-muted-foreground font-mono">{order.invoiceId.slice(-8)}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            {guest ? (
                              <>
                                <div className="text-sm">{guest.name ?? '—'}</div>
                                {guest.email && (
                                  <div className="text-xs text-muted-foreground">{guest.email}</div>
                                )}
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs">{orderStatementRef(order) ?? '—'}</span>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {fmtGbp(order.totalAmountPence ?? order.amountPence)}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 items-start">
                              <Badge variant={statusVariant(order.status)}>
                                {statusLabel(order.status)}
                              </Badge>
                              {order.tld && (
                                <Badge variant={order.tld === 'org' ? 'outline' : 'secondary'} className="text-[10px] px-1 py-0">
                                  .{order.tld}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void openDetail(order)}
                                title="View details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {order.status === 'acceptedComplete' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setRefundOrder(order)}
                                  title="Refund information"
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                              )}
                              {order.poId
                                && !TERMINAL_STATUSES.includes(order.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => void handleRefreshStatus(order)}
                                  disabled={refreshingPoId === order.poId}
                                  title="Refresh status from payment provider"
                                >
                                  {refreshingPoId === order.poId ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {VOIDABLE.includes(order.status) && order.poId && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                  onClick={() => void handleVoid(order)}
                                  disabled={voidingPoId === order.poId}
                                  title="Void order"
                                >
                                  {voidingPoId === order.poId ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Ban className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* ─── Detail dialog ─────────────────────────────────────────────────── */}
      <Dialog open={!!detailOrder} onOpenChange={(open) => { if (!open) setDetailOrder(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order details</DialogTitle>
            <DialogDescription>
              Full record from <code>slickpayOrders/{detailOrder?.poId ?? detailOrder?.requestId}</code>
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : detailOrder ? (
            <div className="space-y-3 text-sm">

              <div className="rounded-md border p-4 space-y-2">
                <p className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-3">Identity</p>
                {detailOrder.poId && <DetailRow label="PO ID" value={<code className="text-xs">{detailOrder.poId}</code>} />}
                {detailOrder.requestId && <DetailRow label="Request ID" value={<code className="text-xs">{detailOrder.requestId}</code>} />}
                <DetailRow label="Type" value={typeLabel(detailOrder.type)} />
                <DetailRow label="Unit" value={detailUnitName || detailOrder.unitId} />
                {detailOrder.invoiceId && <DetailRow label="Invoice ID" value={<code className="text-xs">{detailOrder.invoiceId}</code>} />}
                {detailOrder.eventId && <DetailRow label="Event ID" value={<code className="text-xs">{detailOrder.eventId}</code>} />}
                {detailOrder.packId && <DetailRow label="Pack ID" value={detailOrder.packId} />}
                {detailOrder.credits != null && <DetailRow label="Credits" value={detailOrder.credits} />}
              </div>

              <div className="rounded-md border p-4 space-y-2">
                <p className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-3">Payment</p>
                <DetailRow label="Amount" value={fmtGbp(detailOrder.amountPence)} />
                {detailOrder.processingFeePence != null && (
                  <DetailRow label="Processing fee" value={fmtGbp(detailOrder.processingFeePence)} />
                )}
                {detailOrder.totalAmountPence != null && (
                  <DetailRow label="Total charged" value={fmtGbp(detailOrder.totalAmountPence)} />
                )}
                {detailOrder.paidAmountPence != null && (
                  <DetailRow label="Paid amount (PIS)" value={fmtGbp(detailOrder.paidAmountPence)} />
                )}
                <DetailRow label="Statement ref" value={orderStatementRef(detailOrder)} />
                <DetailRow label="Creditor ID" value={detailOrder.creditorId} />
                {detailOrder.creditorAccountId && (
                  <DetailRow
                    label="Creditor account ID"
                    value={<code className="text-xs break-all">{detailOrder.creditorAccountId}</code>}
                  />
                )}
                {(() => {
                  const destination = resolveDestinationBankDetails(detailOrder, detailUnitPaymentMethods);
                  return (
                    <>
                      <DetailRow label="Name on account" value={destination.nameOnAccount} />
                      <DetailRow
                        label="Sort code"
                        value={destination.sortCode ? formatSortCodeDisplay(destination.sortCode) : undefined}
                      />
                      <DetailRow label="Account number" value={destination.accountNumber} />
                    </>
                  );
                })()}
                {detailOrder.pisApplicationId && (
                  <DetailRow
                    label="PIS Application ID"
                    value={<code className="text-xs break-all">{detailOrder.pisApplicationId}</code>}
                  />
                )}
                {detailOrder.pisCredentialSource && (
                  <DetailRow
                    label="Credential source"
                    value={
                      <Badge
                        variant={detailOrder.pisCredentialSource === 'secret_manager' ? 'secondary' : 'destructive'}
                      >
                        {detailOrder.pisCredentialSource === 'secret_manager'
                          ? 'Google Secret Manager'
                          : detailOrder.pisCredentialSource === 'firestore'
                            ? 'Admin Platform Settings'
                            : detailOrder.pisCredentialSource === 'environment'
                              ? 'Environment variable (misconfigured)'
                              : 'Unknown'}
                      </Badge>
                    }
                  />
                )}
                {(() => {
                  const guest = resolveGuest(detailOrder, guestInfo);
                  return guest?.name ? <DetailRow label="Guest" value={guest.name} /> : null;
                })()}
                {detailOrder.debtorEmailAddress && (
                  <DetailRow label="Debtor email" value={detailOrder.debtorEmailAddress} />
                )}
              </div>

              <div className="rounded-md border p-4 space-y-2">
                <p className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-3">Status</p>
                <DetailRow
                  label="Status"
                  value={<Badge variant={statusVariant(detailOrder.status)}>{statusLabel(detailOrder.status)}</Badge>}
                />
                {detailOrder.tld && (
                  <DetailRow
                    label="Environment"
                    value={
                      <Badge variant={detailOrder.tld === 'org' ? 'outline' : 'secondary'}>
                        {detailOrder.tld === 'org' ? 'Dev (.org)' : 'Production (.com)'}
                      </Badge>
                    }
                  />
                )}
                <DetailRow label="Created" value={fmtDate(detailOrder.createdAt)} />
                {detailOrder.completedAt && <DetailRow label="Completed" value={fmtDate(detailOrder.completedAt)} />}
                {detailOrder.voidedAt && <DetailRow label="Voided" value={fmtDate(detailOrder.voidedAt)} />}
                {detailOrder.updatedAt && <DetailRow label="Last updated" value={fmtDate(detailOrder.updatedAt)} />}
                {detailOrder.initiatedByUserId && (
                  <DetailRow label="Initiated by" value={<code className="text-xs">{detailOrder.initiatedByUserId}</code>} />
                )}
                {detailOrder.voidedByAdminUid && (
                  <DetailRow label="Voided by admin" value={<code className="text-xs">{detailOrder.voidedByAdminUid}</code>} />
                )}
                {detailOrder.pisStep && (
                  <DetailRow label="Failed at step" value={detailOrder.pisStep} />
                )}
                {detailOrder.error && (
                  <DetailRow label="Error" value={<span className="text-destructive text-xs">{detailOrder.error}</span>} />
                )}
                {detailOrder.pisErrorCode != null && (
                  <DetailRow label="PIS error code" value={<code className="text-xs">{detailOrder.pisErrorCode}</code>} />
                )}
                {detailOrder.pisErrorName && (
                  <DetailRow label="PIS error name" value={<code className="text-xs">{detailOrder.pisErrorName}</code>} />
                )}
                {detailOrder.pisErrorMessage && (
                  <DetailRow label="PIS message" value={detailOrder.pisErrorMessage} />
                )}
                {detailOrder.pisStatusCode != null && (
                  <DetailRow label="PIS HTTP status" value={String(detailOrder.pisStatusCode)} />
                )}
                {detailOrder.pisError != null && (
                  <DetailRow
                    label="PIS raw response"
                    value={<pre className="text-xs whitespace-pre-wrap break-all">{JSON.stringify(detailOrder.pisError, null, 2)}</pre>}
                  />
                )}
                {detailOrder.note && (
                  <DetailRow label="Note" value={detailOrder.note} />
                )}
                {detailOrder.pisRawStatus && detailOrder.pisRawStatus !== detailOrder.status && (
                  <DetailRow label="PIS raw status" value={statusLabel(detailOrder.pisRawStatus as PoStatus)} />
                )}
              </div>

              <div className="rounded-md border p-4 space-y-3">
                <p className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Payment timeline</p>
                <p className="text-xs text-muted-foreground">
                  Platform activity (checkout, polls, admin actions) and Hope Macy PIS history when available.
                </p>
                {detailActivity.length === 0 && detailPisHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No activity recorded yet. Use refresh to poll PIS and append events.</p>
                ) : (
                  <ol className="space-y-2 border-l-2 border-muted pl-4">
                    {detailActivity.map((entry) => (
                      <li key={entry.id ?? `${entry.action}-${String(entry.at)}`} className="text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{activityActionLabel(entry.action)}</span>
                          {entry.pisStatus && (
                            <Badge variant="outline" className="text-[10px]">{statusLabel(entry.pisStatus as PoStatus)}</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">{fmtDate(entry.at)}</span>
                        </div>
                        {entry.message && <p className="text-xs text-muted-foreground mt-0.5">{entry.message}</p>}
                        {entry.actorLabel && (
                          <p className="text-[10px] text-muted-foreground">By {entry.actorLabel}</p>
                        )}
                      </li>
                    ))}
                    {detailPisHistory.map((row, i) => (
                      <li key={`pis-${row.time}-${row.event}-${i}`} className="text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{pisEventLabel(row.event)}</span>
                          <Badge variant="secondary" className="text-[10px]">Hope Macy</Badge>
                          <span className="text-xs text-muted-foreground">{row.time ? fmtDate(row.time) : '—'}</span>
                        </div>
                        {row.details && Object.keys(row.details).length > 0 && (
                          <pre className="text-[10px] text-muted-foreground mt-0.5 whitespace-pre-wrap">
                            {JSON.stringify(row.details)}
                          </pre>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {(detailOrder.diagnosticReport || detailOrder.requestId) && (
                <div className="rounded-md border p-4 space-y-2">
                  <p className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-3">Support diagnostic</p>
                  {detailOrder.diagnosticEgressIp != null && (
                    <DetailRow
                      label="Observed egress IP"
                      value={
                        detailOrder.diagnosticEgressIpMatch
                          ? `${detailOrder.diagnosticEgressIp} (matches expected)`
                          : `${detailOrder.diagnosticEgressIp ?? '—'} (expected ${detailOrder.diagnosticExpectedEgressIp ?? '35.214.93.168'})`
                      }
                    />
                  )}
                  {detailOrder.diagnosticReport ? (
                    <pre className="text-xs whitespace-pre-wrap break-all rounded-md border bg-muted/40 p-3 max-h-48 overflow-y-auto">
                      {detailOrder.diagnosticReport}
                    </pre>
                  ) : null}
                </div>
              )}

              {/* Actions within detail dialog */}
              <div className="flex gap-2 pt-2 flex-wrap">
                {detailOrder.requestId && (
                  <Button
                    variant="outline"
                    onClick={() => void handleRunDiagnostic({ ...detailOrder, requestId: detailOrder.requestId })}
                    disabled={runningDiagnostic}
                  >
                    {runningDiagnostic ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Run diagnostic and copy report
                  </Button>
                )}
                {detailOrder.poId && !TERMINAL_STATUSES.includes(detailOrder.status) && (
                  <Button
                    variant="outline"
                    onClick={() => void handleRefreshStatus(detailOrder)}
                    disabled={refreshingPoId === detailOrder.poId}
                  >
                    {refreshingPoId === detailOrder.poId ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Refresh from PIS
                  </Button>
                )}
                {detailOrder.status === 'acceptedComplete' && (
                  <Button
                    variant="outline"
                    onClick={() => { setRefundOrder(detailOrder); setDetailOrder(null); }}
                  >
                    <Info className="mr-2 h-4 w-4" />
                    Refund information
                  </Button>
                )}
                {VOIDABLE.includes(detailOrder.status) && detailOrder.poId && (
                  <Button
                    variant="destructive"
                    onClick={() => { void handleVoid(detailOrder); setDetailOrder(null); }}
                    disabled={voidingPoId === detailOrder.poId}
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    Void order
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ─── Refund info dialog ────────────────────────────────────────────── */}
      <Dialog open={!!refundOrder} onOpenChange={(open) => { if (!open) setRefundOrder(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Refund information</DialogTitle>
            <DialogDescription>
              Open Banking payments cannot be automatically reversed once settled.
            </DialogDescription>
          </DialogHeader>
          {refundOrder && (
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                The Faster Payments bank transfer for this order has already been accepted and credited to the
                receiving account. To issue a refund you must manually transfer the amount back to the payer.
              </p>

              <div className="rounded-md border p-4 space-y-2 bg-muted/40">
                <p className="font-semibold">Order summary</p>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount paid</span>
                  <span className="font-medium">{fmtGbp(refundOrder.paidAmountPence ?? refundOrder.totalAmountPence ?? refundOrder.amountPence)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Statement reference</span>
                  <span className="font-mono text-xs">{orderStatementRef(refundOrder) ?? '—'}</span>
                </div>
                {refundOrder.debtorEmailAddress && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Debtor email</span>
                    <span>{refundOrder.debtorEmailAddress}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unit</span>
                  <span>{unitLabels[refundOrder.unitId] ?? refundOrder.unitId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">PO ID</span>
                  <span className="font-mono text-xs">{refundOrder.poId}</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Contact the paying unit or the debtor directly to arrange the return of funds. Use the creditor
                reference and PO ID above for reconciliation.
              </p>

              <Button variant="outline" className="w-full" onClick={() => setRefundOrder(null)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
